import { queryOptions, useQuery } from '@tanstack/react-query';

import type { AccessToken } from '@/lib/tokenStorage';
import { queryClient } from '@/services/api/query/queryClient';
import { useAccessToken } from '@/services/auth';

export function featureFlagsQueryOptions(token: AccessToken) {
  return queryOptions({
    queryKey: ['featureFlags'],
    enabled: !!token,
    async queryFn() {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/api/features`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          credentials: 'include',
        },
      );

      if (!response.ok) {
        throw new Error('HTTP Error');
      }

      const flags = await response.json();
      return flags as Record<string, boolean | undefined>;
    },
    meta: { persist: true },
  });
}

export function useFeatureFlags() {
  const token = useAccessToken();
  const flags = useQuery(featureFlagsQueryOptions(token));
  return flags.data ?? {};
}

export function useFeatureFlag(flagName: string): boolean {
  const flags = useFeatureFlags();
  return flags[flagName] ?? false;
}

export function getFeatureFlag(flagName: string): boolean {
  const flags = queryClient.getQueryData<Record<string, boolean | undefined>>(
    featureFlagsQueryOptions(null).queryKey,
  );
  return flags?.[flagName] ?? false;
}
